import { injectAxios } from '@/lib/axios/Axios';

import countriesNames from '../../data/country-names-en.json';

import { LocalStorageUtility } from './localstorage.util';

type CountryCode = keyof typeof countriesNames;

type PlatformData = {
  name: string;
  value: string;
  version: string;
}

type StoredGeoInfo = {
  ip: string;
  countryCode: string;
}

type GeoInfoResponse = {
  geo: {
    city: string;
    country: string;
    region: string;
  },
  ip: string | 'unknown';
}

const euCountryCodes = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GB',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
];

const eeaCountryCodes = new Set([
  ...euCountryCodes,
  'IS',
  'LI',
  'NO',
]);

const dataos = [
  { name: 'Windows Phone', value: 'Windows Phone', version: 'OS' },
  { name: 'Windows', value: 'Win', version: 'NT' },
  { name: 'iPhone', value: 'iPhone', version: 'OS' },
  { name: 'iPad', value: 'iPad', version: 'OS' },
  { name: 'Kindle', value: 'Silk', version: 'Silk' },
  { name: 'Android', value: 'Android', version: 'Android' },
  { name: 'PlayBook', value: 'PlayBook', version: 'OS' },
  { name: 'BlackBerry', value: 'BlackBerry', version: '/' },
  { name: 'Macintosh', value: 'Mac', version: 'OS X' },
  { name: 'Linux', value: 'Linux', version: 'rv' },
  { name: 'Palm', value: 'Palm', version: 'PalmOS' },
];

/** In dev: clear geo cache once per page load so mock country changes (e.g. US vs GB) take effect. */
let devGeoCacheClearedThisSession = false;

const GEO_INFO_KEY = 'geo-info';

/** Call before any read of geo cache in dev so first paint never uses stale cache (e.g. after changing mock). */
export function clearGeoCacheOnceInDev(): void {
  if (process.env.NODE_ENV === 'development' && !devGeoCacheClearedThisSession) {
    devGeoCacheClearedThisSession = true;
    localStorage.removeItem(GEO_INFO_KEY);
  }
}

class DeviceAndGeoDetector {
  static hasPendingRequest = false;

  static header = () => [
    navigator?.platform,
    navigator?.userAgent,
    navigator?.appVersion,
    navigator?.vendor,
  ];

  /**
   * Normalizes country codes to handle variations (e.g., 'UK' -> 'GB')
   */
  static normalizeCountryCode(countryCode: string): string {
    if (!countryCode || countryCode === 'unknown') return 'unknown';
    // Normalize 'UK' to 'GB' as some APIs return 'UK' instead of 'GB'
    if (countryCode.toUpperCase() === 'UK') return 'GB';
    return countryCode.toUpperCase();
  }

  /**
   * Checks if a country change is significant (e.g., EU ↔ non-EU)
   * This helps detect VPN usage or location changes that should invalidate cache
   */
  static isSignificantCountryChange(oldCountry: string, newCountry: string): boolean {
    if (!oldCountry || !newCountry || oldCountry === 'unknown' || newCountry === 'unknown') {
      return false;
    }
    const normalizedOld = this.normalizeCountryCode(oldCountry);
    const normalizedNew = this.normalizeCountryCode(newCountry);
    
    if (normalizedOld === normalizedNew) {
      return false;
    }
    
    const oldIsEU = this.isCountryInEU(normalizedOld);
    const newIsEU = this.isCountryInEU(normalizedNew);
    const oldIsEEA = this.isCountryInEEAOrUK(normalizedOld);
    const newIsEEA = this.isCountryInEEAOrUK(normalizedNew);
    
    // Significant change: EU ↔ non-EU or EEA ↔ non-EEA
    return (oldIsEU !== newIsEU) || (oldIsEEA !== newIsEEA);
  }

  static async getGeoLocData(forceRefresh = false, checkSignificantChange = false): Promise<StoredGeoInfo> {
    if (this.hasPendingRequest) {
      return new Promise((resolve) => {
        const intervalId = setInterval(() => {
          if (!this.hasPendingRequest) {
            clearInterval(intervalId);
            resolve({
              ip: this.ipAddr,
              countryCode: this.countryCode,
            });
          }
        }, 80);
      });
    }

    try {
      clearGeoCacheOnceInDev();
      let storedGeoInfo: StoredGeoInfo | null = LocalStorageUtility.getItemWithExpiry(GEO_INFO_KEY);
      
      // If we have cached data and checking for significant changes (e.g., during onboarding)
      if (storedGeoInfo && !forceRefresh && checkSignificantChange) {
        // Normalize stored country code in case it was stored before normalization
        storedGeoInfo.countryCode = this.normalizeCountryCode(storedGeoInfo.countryCode);
        
        // Fetch fresh geo data to compare for significant changes
        this.hasPendingRequest = true;
        try {
          const { data: responseData } = await injectAxios(`${ process.env.NEXT_PUBLIC_VERCEL_HOST_URL }/api`)
            .get<GeoInfoResponse>(`/geo-info`);
          
          const freshCountryCode = this.normalizeCountryCode(responseData.geo.country);
          
          // If there's a significant country change (e.g., VPN detected), use fresh data and update cache
          if (this.isSignificantCountryChange(storedGeoInfo.countryCode, freshCountryCode)) {
            this.hasPendingRequest = false;
            const newGeoInfo: StoredGeoInfo = {
              ip: responseData.ip,
              countryCode: freshCountryCode,
            };
            LocalStorageUtility.setItemWithExpiry(GEO_INFO_KEY, newGeoInfo, 7);
            return newGeoInfo;
          }
          
          // No significant change, use cached data
          this.hasPendingRequest = false;
          return storedGeoInfo;
        } catch (e) {
          // If fresh fetch fails, use cached data
          this.hasPendingRequest = false;
          return storedGeoInfo;
        }
      }

      // Use cached data if available and not forcing refresh
      if (storedGeoInfo && !forceRefresh) {
        // Normalize stored country code in case it was stored before normalization
        storedGeoInfo.countryCode = this.normalizeCountryCode(storedGeoInfo.countryCode);
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log('[DeviceAndGeoDetector] getGeoLocData: using cache', {
            countryCode: storedGeoInfo.countryCode,
            isEU: this.isCountryInEU(storedGeoInfo.countryCode),
            isEEAOrUK: this.isCountryInEEAOrUK(storedGeoInfo.countryCode),
          });
        }
        return storedGeoInfo;
      }

      // No cached data or force refresh requested
      if (storedGeoInfo && forceRefresh) {
        // Clear cache when forcing refresh
        localStorage.removeItem(GEO_INFO_KEY);
      }

      this.hasPendingRequest = true;
      const geoApiBase = `${ process.env.NEXT_PUBLIC_VERCEL_HOST_URL }/api`;
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('[DeviceAndGeoDetector] getGeoLocData: fetching geo from', geoApiBase);
      }
      const { data: responseData } = await injectAxios(geoApiBase)
        .get<GeoInfoResponse>(`/geo-info`);

      this.hasPendingRequest = false;
      const normalizedCountryCode = this.normalizeCountryCode(responseData.geo.country);
      storedGeoInfo = {
        ip: responseData.ip,
        countryCode: normalizedCountryCode,
      };
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('[DeviceAndGeoDetector] getGeoLocData: API response', {
          rawCountry: responseData.geo?.country,
          normalizedCountryCode,
          isEU: this.isCountryInEU(normalizedCountryCode),
          isEEAOrUK: this.isCountryInEEAOrUK(normalizedCountryCode),
        });
      }
      LocalStorageUtility.setItemWithExpiry(GEO_INFO_KEY, storedGeoInfo, 7);
      return storedGeoInfo;
    } catch (e) {
      this.hasPendingRequest = false;
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('[DeviceAndGeoDetector] getGeoLocData: fetch failed, using unknown', e);
      }
      return {
        ip: 'unknown',
        countryCode: 'unknown',
      };
    }
  }

  static countryCode = '';
  static ipAddr = '';
  static isMobile = () => window.matchMedia('(hover: none), (pointer: coarse)').matches
    && (/Android|Iphone|HarmonyOS/i).test(window.navigator.userAgent);

  static async initialize(checkSignificantChange = false) {
    const agent = this.header().join(' ');
    const osName = this.matchDevice(agent, dataos);
    const userAgent = window.navigator.userAgent;
    const width = (window.innerWidth > 0) ? window.innerWidth : screen.width;
    const height = (window.innerHeight > 0) ? window.innerHeight : screen.height;
    const ismobile = this.isMobile();

    if (!this.ipAddr || !this.countryCode) {
      const { ip, countryCode } = await this.getGeoLocData(false, checkSignificantChange);

      // Cache the geo data so that we don't retrieve the IP for every track request
      this.ipAddr = ip;
      this.countryCode = this.normalizeCountryCode(countryCode);
    } else if (checkSignificantChange) {
      // Even if we have cached values, check for significant changes when requested
      const { ip, countryCode } = await this.getGeoLocData(false, checkSignificantChange);
      this.ipAddr = ip;
      this.countryCode = this.normalizeCountryCode(countryCode);
    }

    return {
      width,
      height,
      deviceinfo: { os: osName, browser: this.browserDetectionName(userAgent) },
      ismobile,
      ipaddress: this.ipAddr,
      country: this.countryCode,
    };
  }

  static isChromeBrowser(): boolean {
    return DeviceAndGeoDetector.browserDetectionName(window.navigator.userAgent) === 'Google Chrome';
  }

  static browserDetectionName(userAgent: string) {
    const browserName = (regexp: RegExp) => regexp.test(userAgent);

    switch (true) {
      case browserName(/edg/i):
        return 'Microsoft Edge';
      case browserName(/trident/i):
        return 'Microsoft Internet Explorer';
      case browserName(/firefox|fxios/i):
        return 'Mozilla Firefox';
      case browserName(/opr\//i):
        return 'Opera';
      case browserName(/ucbrowser/i):
        return 'UC Browser';
      case browserName(/samsungbrowser/i):
        return 'Samsung Browser';
      case browserName(/chrome|chromium|crios/i):
        return 'Google Chrome';
      case browserName(/safari/i):
        return 'Apple Safari';
      default:
        return 'Other';
    }
  }

  static matchDevice(agent: string, data: PlatformData[]) {
    for (const datum of data) {
      const regex = new RegExp(datum.value, 'i');
      const match = regex.test(agent);

      if (match) {
        return datum.name;
      }
    }

    return 'unknown';
  }

  static isCountryInEU = (countryCode: string) => {
    const normalized = this.normalizeCountryCode(countryCode);
    if (!normalized || normalized === 'unknown') return false;
    return euCountryCodes.includes(normalized);
  };
  
  static isCountryInEEAOrUK = (countryCode: string) => {
    const normalized = this.normalizeCountryCode(countryCode);
    if (!normalized || normalized === 'unknown') return false;
    return eeaCountryCodes.has(normalized);
  };
  
  static getCountryName = (countryCode: string) => countriesNames[countryCode as CountryCode];

  /**
   * Returns geo-related tracking properties that should always be included.
   * Ensures isEuMember and EEA properties are always present, even when country is unknown.
   */
  static getGeoTrackingProperties = async () => {
    const geoInfo = await this.initialize();
    const countryCode = geoInfo.country || 'unknown';
    const isEuMember = this.isCountryInEU(countryCode);
    const isEea = this.isCountryInEEAOrUK(countryCode);

    return {
      ...geoInfo,
      isEuMember,
      EEA: isEea,
      // Keep backward compatibility
      iseuaccount: isEuMember,
    };
  };
}

export default DeviceAndGeoDetector;
